// public/js/features/settings/derived-rule-editor.js
// Shared editor widget for a list of derived-sensor rules. Used by the tenant
// Settings tab (settings-derived-sensors.js) and the per-asset override section
// on the asset detail page. Keeps one copy of the card markup + validation so the
// two callers can never drift. See docs/derived-sensors.md.
import { helpIcon, initHelp } from '../../ui/help.js';

// Source metrics a rule may read (kept in sync with utils/derived-sensors.mjs
// DERIVED_SOURCE_METRICS). Label is display-only.
export const SOURCE_METRICS = [
  { key: 'distanceM', label: 'Distance (m)' },
  { key: 'rangeMm', label: 'Range (mm)' },
  { key: 'temperature', label: 'Temperature' },
  { key: 'humidity', label: 'Humidity' },
  { key: 'pressure', label: 'Pressure' },
  { key: 'probe1Temp', label: 'Probe 1 temp' },
  { key: 'probe2Temp', label: 'Probe 2 temp' },
  { key: 'fuelLevel', label: 'Fuel level' },
  { key: 'battery', label: 'Battery (V)' },
  { key: 'batteryPercent', label: 'Battery (%)' },
  { key: 'rssi', label: 'Signal (RSSI)' },
  { key: 'rssiAvg', label: 'Signal level (RSSI avg)' },
  { key: 'rssiSpread', label: 'Signal spread (RSSI)' },
  { key: 'rssiFloor', label: 'Signal floor (RSSI)' },
  { key: 'magnetCount', label: 'Magnet count' },
];

export const RULE_EDITOR_HELP = {
  derivedKey:
    '<b>Key</b><br>' +
    'The short, permanent id of this virtual sensor (letters, digits, underscore). It is how ' +
    'the value is stored and referenced in alerts. Pick it once and do not change it — renaming ' +
    'it starts a fresh history under the new key.',
  sourceMetric:
    '<b>Reads</b><br>The raw sensor field this rule watches.',
  threshold:
    '<b>Threshold</b><br>The value the reading is compared against, in the raw metric\'s own unit.',
  activeWhen:
    '<b>Active state (1) when</b><br>' +
    'Which side of the threshold counts as the "on" state. <b>Below</b> = active when the reading ' +
    'is under the threshold (e.g. loaded = distance below 50 cm); <b>at or above</b> = active when ' +
    'it reaches or passes the threshold (e.g. hot = temperature at or above 60 °C).',
  labels:
    '<b>State labels</b><br>The words shown for the two states — active (1) and inactive (0).',
  isDefault:
    '<b>Default</b><br>' +
    'On = this rule runs automatically on every sensor in the tenant that reads its metric. ' +
    'Off = it is only a <b>template</b> you can pick per sensor on an asset\'s detail page. ' +
    'Either way, a single sensor can override the rule\'s threshold/direction just for itself.',
  hysteresis:
    '<b>Anti-flapping (optional)</b><br>' +
    'When a reading hovers right at the threshold it can toggle rapidly. Turn this on to use two ' +
    'edges: the state switches ON at one value and only back OFF at another, so it holds steady in ' +
    'between. Leave off for a single crisp threshold.' +
    '<br><br><b>Aviation:</b> a fuel-level state that flips on at 20 % and off at 25 % will not ' +
    'chatter while a truck sloshes around 22 %.',
};

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const metricLabel = (k) => (SOURCE_METRICS.find(m => m.key === k)?.label) || k || '—';

// One-line summary shown in a collapsed card header (built from a rule object).
function ruleSummary(r = {}) {
  const name = r.label || r.derivedKey || 'New rule';
  const key = r.derivedKey ? ` <code class="text-muted">${esc(r.derivedKey)}</code>` : '';
  if (!r.derivedKey && r.threshold == null) return `<span class="fw-semibold">New template</span>`;
  const side = r.activeWhen === 'below' ? 'below' : 'at/above';
  const hy = r.hysteresis && r.hysteresis.on != null ? ' · anti-flap' : '';
  const badge = r.isDefault
    ? `<span class="badge rounded-pill text-bg-primary ms-1" style="font-size:.6rem;">Default</span>`
    : `<span class="badge rounded-pill text-bg-light text-dark border ms-1" style="font-size:.6rem;">Template</span>`;
  return `<span class="fw-semibold">${esc(name)}</span>${key}${badge} <span class="text-muted small">· ${esc(metricLabel(r.sourceMetric))} ${side} ${r.threshold != null ? esc(r.threshold) : '?'}${hy}</span>`;
}

// One editable rule card. `r` may be a partial/new rule. `collapsed` starts the
// body hidden (existing rules load collapsed so a long list stays scannable).
function ruleCard(r = {}, collapsed = false) {
  const hyOn = r.hysteresis && r.hysteresis.on != null;
  return `
    <div class="border rounded p-3 mb-3 ds-rule" data-rule-id="${esc(r.ruleId || '')}">
      <div class="d-flex justify-content-between align-items-center ds-head" role="button">
        <span class="d-flex align-items-center gap-2 flex-grow-1">
          <i class="fa-solid ${collapsed ? 'fa-chevron-right' : 'fa-chevron-down'} ds-chevron text-secondary"></i>
          <span class="ds-summary">${ruleSummary(r)}</span>
        </span>
        <button type="button" class="btn btn-sm btn-outline-danger ds-del ms-2"><i class="fa-solid fa-trash"></i></button>
      </div>
      <div class="row g-2 ds-body ${collapsed ? 'd-none' : ''} mt-1">
        <div class="col-md-4">
          <label class="form-label small mb-1">Name</label>
          <input class="form-control form-control-sm ds-label" value="${esc(r.label || '')}" placeholder="Load state">
        </div>
        <div class="col-md-4">
          <label class="form-label small mb-1">Key${helpIcon('derivedKey')}</label>
          <input class="form-control form-control-sm ds-key" value="${esc(r.derivedKey || '')}" placeholder="loadState">
        </div>
        <div class="col-md-4">
          <label class="form-label small mb-1">Reads${helpIcon('sourceMetric')}</label>
          <select class="form-select form-select-sm ds-metric">
            ${SOURCE_METRICS.map(m => `<option value="${m.key}" ${m.key === r.sourceMetric ? 'selected' : ''}>${esc(m.label)}</option>`).join('')}
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-1">Threshold${helpIcon('threshold')}</label>
          <input type="number" step="any" class="form-control form-control-sm ds-threshold" value="${r.threshold != null ? esc(r.threshold) : ''}" placeholder="0.5">
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-1">Active (1) when${helpIcon('activeWhen')}</label>
          <select class="form-select form-select-sm ds-active">
            <option value="atOrAbove" ${r.activeWhen !== 'below' ? 'selected' : ''}>at or above</option>
            <option value="below" ${r.activeWhen === 'below' ? 'selected' : ''}>below</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-1">Active label${helpIcon('labels')}</label>
          <input class="form-control form-control-sm ds-labelActive" value="${esc(r.labelActive || '')}" placeholder="loaded">
        </div>
        <div class="col-md-3">
          <label class="form-label small mb-1">Inactive label</label>
          <input class="form-control form-control-sm ds-labelInactive" value="${esc(r.labelInactive || '')}" placeholder="empty">
        </div>
        <div class="col-12 mt-2">
          <div class="form-check form-switch">
            <input class="form-check-input ds-default" type="checkbox" role="switch" ${r.isDefault ? 'checked' : ''}>
            <label class="form-check-label small">Default — apply automatically to every sensor that reads this metric${helpIcon('isDefault')}</label>
          </div>
          <div class="text-muted" style="font-size:.72rem;">Off = a selectable template only; a sensor gets it when you pick it on the asset.</div>
        </div>
        <div class="col-12 mt-2">
          <div class="form-check form-switch">
            <input class="form-check-input ds-hy-on" type="checkbox" role="switch" ${hyOn ? 'checked' : ''}>
            <label class="form-check-label small">Anti-flapping (two edges)${helpIcon('hysteresis')}</label>
          </div>
          <div class="row g-2 ds-hy-fields ${hyOn ? '' : 'd-none'} mt-1" style="max-width:420px;">
            <div class="col-6">
              <label class="form-label small mb-1">Switch ON at</label>
              <input type="number" step="any" class="form-control form-control-sm ds-hy-onval" value="${hyOn ? esc(r.hysteresis.on) : ''}">
            </div>
            <div class="col-6">
              <label class="form-label small mb-1">Switch OFF at</label>
              <input type="number" step="any" class="form-control form-control-sm ds-hy-offval" value="${hyOn ? esc(r.hysteresis.off) : ''}">
            </div>
          </div>
        </div>
      </div>
    </div>`;
}

// Mount an editable rule list into `host`. Renders the cards + an "Add rule"
// button; the caller supplies its own Save button and calls read() on click.
//   opts.emptyText  – message shown when there are no rules
// Returns { read() } where read() → { rules } or { error }.
export function mountRuleEditor(host, initialRules = [], opts = {}) {
  const emptyText = opts.emptyText || 'No rules yet. Add one below.';
  const rules = Array.isArray(initialRules) ? initialRules : [];

  host.innerHTML = `
    <div class="ds-rules">
      ${rules.length ? rules.map(r => ruleCard(r, true)).join('') : `<div class="text-muted small mb-3 ds-empty">${esc(emptyText)}</div>`}
    </div>
    <button type="button" class="btn btn-outline-primary btn-sm ds-add"><i class="fa-solid fa-plus me-1"></i>Add rule</button>`;

  initHelp(host, RULE_EDITOR_HELP);
  const rulesBox = host.querySelector('.ds-rules');

  // Read one card's inputs into a partial rule (for the collapsed summary).
  const cardToRule = (card) => ({
    derivedKey: card.querySelector('.ds-key').value.trim(),
    label: card.querySelector('.ds-label').value.trim(),
    sourceMetric: card.querySelector('.ds-metric').value,
    threshold: card.querySelector('.ds-threshold').value.trim() === '' ? null : Number(card.querySelector('.ds-threshold').value),
    activeWhen: card.querySelector('.ds-active').value,
    isDefault: card.querySelector('.ds-default')?.checked === true,
    hysteresis: card.querySelector('.ds-hy-on').checked ? { on: Number(card.querySelector('.ds-hy-onval').value) } : null,
  });

  const setCollapsed = (card, collapsed) => {
    card.querySelector('.ds-body')?.classList.toggle('d-none', collapsed);
    const chev = card.querySelector('.ds-chevron');
    if (chev) { chev.classList.toggle('fa-chevron-right', collapsed); chev.classList.toggle('fa-chevron-down', !collapsed); }
    if (collapsed) card.querySelector('.ds-summary').innerHTML = ruleSummary(cardToRule(card));
  };

  const wireCard = (card) => {
    // Header toggles collapse (but not when the delete button is clicked).
    card.querySelector('.ds-head')?.addEventListener('click', (e) => {
      if (e.target.closest('.ds-del')) return;
      setCollapsed(card, !card.querySelector('.ds-body')?.classList.contains('d-none'));
    });
    card.querySelector('.ds-del')?.addEventListener('click', (e) => {
      e.stopPropagation();
      card.remove();
      if (!rulesBox.querySelector('.ds-rule')) {
        rulesBox.innerHTML = `<div class="text-muted small mb-3 ds-empty">${esc(emptyText)}</div>`;
      }
    });
    card.querySelector('.ds-hy-on')?.addEventListener('change', (e) => {
      card.querySelector('.ds-hy-fields')?.classList.toggle('d-none', !e.target.checked);
    });
  };
  host.querySelectorAll('.ds-rule').forEach(wireCard);

  host.querySelector('.ds-add')?.addEventListener('click', () => {
    host.querySelector('.ds-empty')?.remove();
    const tmp = document.createElement('div');
    tmp.innerHTML = ruleCard({}, false);   // new rule starts expanded
    const card = tmp.firstElementChild;
    rulesBox.appendChild(card);
    initHelp(card, RULE_EDITOR_HELP);
    wireCard(card);
  });

  function read() {
    const out = [];
    let error = null;
    host.querySelectorAll('.ds-rule').forEach(card => {
      const key = card.querySelector('.ds-key').value.trim();
      const threshold = card.querySelector('.ds-threshold').value.trim();
      if (!key && threshold === '') return; // skip a wholly empty card
      if (!/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/.test(key)) { error = error || 'Each rule needs a valid Key (letters/digits/underscore, starting with a letter).'; return; }
      if (threshold === '' || !isFinite(Number(threshold))) { error = error || `Rule "${key}" needs a numeric threshold.`; return; }
      const rule = {
        derivedKey: key,
        label: card.querySelector('.ds-label').value.trim() || key,
        sourceMetric: card.querySelector('.ds-metric').value,
        threshold: Number(threshold),
        activeWhen: card.querySelector('.ds-active').value === 'below' ? 'below' : 'atOrAbove',
        isDefault: card.querySelector('.ds-default')?.checked === true,
        labelActive: card.querySelector('.ds-labelActive').value.trim() || 'active',
        labelInactive: card.querySelector('.ds-labelInactive').value.trim() || 'inactive',
      };
      const ruleId = card.dataset.ruleId;
      if (ruleId) rule.ruleId = ruleId;
      if (card.querySelector('.ds-hy-on').checked) {
        const on = Number(card.querySelector('.ds-hy-onval').value);
        const off = Number(card.querySelector('.ds-hy-offval').value);
        if (isFinite(on) && isFinite(off)) rule.hysteresis = { on, off };
      }
      out.push(rule);
    });
    // Keys MAY repeat across templates — e.g. a normal and an inverted variant that
    // both output the same state (engineLoaded) for oppositely-mounted sensors. The
    // only rule: at most ONE Default per key, or a sensor reading that metric would
    // get two conflicting defaults.
    if (!error) {
      const defKeys = out.filter(r => r.isDefault).map(r => r.derivedKey);
      if (new Set(defKeys).size !== defKeys.length) {
        error = 'Two Default templates share the same Key. Only one Default is allowed per Key — turn Default off on the others (they stay selectable templates).';
      }
    }
    return error ? { error } : { rules: out };
  }

  return { read };
}
